import { useEffect, useState, useMemo } from 'react';
import '../styles/components/_animated-background.scss';

const colors = [
  'rgba(255, 107, 107, 0.25)',   // Soft coral
  'rgba(78, 205, 196, 0.25)',    // Teal
  'rgba(69, 183, 209, 0.25)',     // Sky blue
  'rgba(152, 216, 200, 0.22)',  // Mint
  'rgba(247, 220, 111, 0.22)',  // Soft yellow
  'rgba(187, 143, 206, 0.22)',  // Lavender
  'rgba(255, 159, 64, 0.22)',   // Peach
  'rgba(116, 185, 255, 0.22)',  // Light blue
];

const AnimatedBackground = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Generate orbs with different properties
  // Size in rem (200-500px = 12.5-31.25rem)
  const orbs = useMemo(() =>
    Array.from({ length: 8 }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      size: 12.5 + Math.random() * 18.75, // rem equivalent of 200-500px
      delay: Math.random() * 8,
      duration: 15 + Math.random() * 10,
      color: colors[Math.floor(Math.random() * colors.length)],
    })), []
  );

  return (
    <div className="animated-background" data-scroll={scrollY}>
      <div className="animated-background__mesh" />
      <div className="animated-background__grid" />
      <div className="animated-background__orbs">
        {orbs.map((orb) => (
          <div
            key={orb.id}
            className="animated-background__orb"
            style={{
              top: `${orb.top}%`,
              left: `${orb.left}%`,
              width: `${orb.size}rem`,
              height: `${orb.size}rem`,
              backgroundColor: orb.color,
              animationDelay: `${orb.delay}s`,
              animationDuration: `${orb.duration}s`,
              '--scroll-y': `${scrollY * 0.00625}rem`, // 0.1px = 0.00625rem (0.1/16)
            } as React.CSSProperties & { '--scroll-y': string }}
          />
        ))}
      </div>
      <div className="animated-background__waves-svg">
        <svg className="animated-background__wave-svg animated-background__wave-svg--1" viewBox="0 0 2400 200" preserveAspectRatio="none">
          <path
            className="animated-background__wave-path animated-background__wave-path--1"
            d="M0,100 Q300,50 600,100 T1200,100 T1800,100 T2400,100 L2400,200 L0,200 Z"
          />
        </svg>
        <svg className="animated-background__wave-svg animated-background__wave-svg--2" viewBox="0 0 2400 200" preserveAspectRatio="none">
          <path
            className="animated-background__wave-path animated-background__wave-path--2"
            d="M0,120 Q300,70 600,120 T1200,120 T1800,120 T2400,120 L2400,200 L0,200 Z"
          />
        </svg>
        <svg className="animated-background__wave-svg animated-background__wave-svg--3" viewBox="0 0 2400 200" preserveAspectRatio="none">
          <path
            className="animated-background__wave-path animated-background__wave-path--3"
            d="M0,140 Q300,90 600,140 T1200,140 T1800,140 T2400,140 L2400,200 L0,200 Z"
          />
        </svg>
      </div>
      <div className="animated-background__geometric">
        {Array.from({ length: 6 }, (_, i) => (
          <div
            key={i}
            className="animated-background__geometric-shape"
            style={{
              top: `${20 + i * 15}%`,
              left: `${10 + i * 12}%`,
              animationDelay: `${i * 0.8}s`,
            }}
          />
        ))}
      </div>
      <div className="animated-background__overlay" />
    </div>
  );
};

export default AnimatedBackground;
